import * as Yup from 'yup';
import { blue, green, orange, purple, red, teal } from '@mui/material/colors';
import { PATH_DASHBOARD } from 'src/routes/paths';
import {
  getCity,
  getCountry,
  getLine,
  getPostalCode,
  getState,
} from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/Map';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';

export const mapOrganizationToFhir = (
  data: any,
  configurations: any,
  catalogTypeIdentifier: any
): any => {
  const { typeIdentifier, identifier: identifierValue, name, address, type } = data ?? {};

  const mapType = type?.code
    ? {
        coding: [type],
        ...(type?.display && { text: type.display }),
      }
    : undefined;

  const identifier =
    typeIdentifier?.code || identifierValue
      ? [mapIdentifier(catalogTypeIdentifier, configurations, typeIdentifier, identifierValue)]
      : undefined;

  return {
    resourceType: 'Organization',
    name,
    ...(identifier && { identifier }),
    ...(address && { address: [address] }),
    ...(mapType && { type: [mapType] }),
    active: true,
  };
};

export const placeToAddress = (place: any, configurations: any) => {
  const addressArray = place.address_components;

  const mapAddress = {
    id: place.place_id,
    line: [getLine(addressArray)],
    city: getCity(addressArray) || '',
    state: getState(addressArray) || '',
    postalCode: getPostalCode(addressArray) || '',
    country: getCountry(addressArray) || '',
    use: 'work',
    extension: [
      {
        extension: [
          {
            url: 'identifier',
            valueString: place.place_id,
          },
          {
            url: 'valid',
            valueBoolean: true,
          },
          {
            url: 'address',
            valueString: getLine(addressArray),
          },
          {
            url: 'latitude',
            valueDecimal: place.geometry.location.lat(),
          },
          {
            url: 'longitude',
            valueDecimal: place.geometry.location.lng(),
          },
        ],
        url: `${configurations?.fhir?.baseUrl}/StructureDefinition/PH-address-valid`,
      },
    ],
  };

  return mapAddress;
};

const mapIdentifier = (catalog: WrappedValueSet, configurations: any, type: any, value: any) => {
  const getIdentifierType = (catalog?.asList?.() ?? []).map(({ code, display, extension }) => {
    const valueUri = extension?.[0]?.extension?.[0]?.valueUri;
    let systemUri = valueUri || configurations?.fhir?.baseUrl;
    let _code = code;

    if (code.includes('|')) {
      const codeSplit = code.split('|');
      _code = codeSplit[1];
      systemUri = codeSplit[0];
    }

    return {
      system: systemUri,
      type: {
        coding: [{ code: _code, system: catalog?.getConceptSystem() }],
        ...(display && { text: display }),
      },
    };
  });

  const identifierType = getIdentifierType?.find((e) => e?.type?.coding?.[0]?.code === type?.code);

  return {
    value: value || '',
    ...identifierType,
  };
};

export const mapYupErrors = (err: any) => {
  const errors: Record<string, string> = {};

  err.forEach((e: any) => {
    if (e.path && !errors[e.path]) {
      errors[e.path] = e.message;
    }
  });

  return errors;
};

export const validateInputs = async (data: any, translate: any, configurations: any) => {
  const validationRules = configurations?.organization?.rules ?? {};

  const schema = Yup.object({
    name: Yup.string().nullable().required(translate('details.search.rules.name')),
    typeIdentifier: Yup.object().nullable(),
    identifier: Yup.string().nullable(),
    address: Yup.object().nullable(),
    type: Yup.object().nullable(),
  }).test('cross', function (values) {
    const { typeIdentifier, identifier, address, type } = values;

    const errors: any = [];

    if (validationRules?.type && !type?.code) {
      errors.push(
        this.createError({
          path: 'type',
          message: translate('details.search.rules.type'),
        })
      );
    }

    if (validationRules?.address && !address?.id) {
      errors.push(
        this.createError({
          path: 'address',
          message: translate('details.search.rules.address'),
        })
      );
    }

    if (validationRules?.identifier && !identifier) {
      errors.push(
        this.createError({
          path: 'identifier',
          message: translate('details.search.rules.identifier'),
        })
      );
    }

    if (identifier && !typeIdentifier?.code) {
      errors.push(
        this.createError({
          path: 'typeIdentifier',
          message: translate('details.search.rules.typeIdentifier'),
        })
      );
    }

    if (typeIdentifier?.code && !identifier) {
      errors.push(
        this.createError({
          path: 'identifier',
          message: translate('details.search.rules.identifier'),
        })
      );
    }

    if (errors.length) {
      throw new Yup.ValidationError(errors);
    }

    return true;
  });

  const validateFields = await schema
    .validate(data, { abortEarly: false })
    .catch((err) => err.inner);

  if (!validateFields?.length) return null;

  return mapYupErrors(validateFields);
};

const muiColors = [blue[500], green[500], orange[500], purple[500], red[500], teal[500]];

const hashString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

export const getColorByKey = (key: string) => {
  const index = hashString(key) % muiColors.length;
  return muiColors[index];
};

export const getGoToSection = (type: string, patientId: string) => {
  const pathPatient = PATH_DASHBOARD.crs.patient.details.forId(patientId);
  switch (type) {
    case 'ehub-box':
      return `${pathPatient}/engagement-hub`;
    case 'case':
      return `${pathPatient}/referrals-cases`;
    case 'referral':
      return `${pathPatient}/referrals-cases`;
    case 'serviceRequest':
      return `${pathPatient}/referrals-cases`;
    case 'task':
      return `${pathPatient}/tasks`;
    case 'episodeOfCare':
      return `${pathPatient}/health`;
    case 'generalPractitioner':
      return `${pathPatient}/group`;
    default:
      return '';
  }
};
